import { Slot } from '@radix-ui/react-slot';
import {
  type ComponentPropsWithoutRef,
  type KeyboardEvent,
  type MouseEvent,
  memo,
  type ReactNode,
  useCallback,
  useState,
} from 'react';

import { ContextMenu, renderContextMenuItems } from '@air/primitive-context-menu';
import { cn } from '@air/utils-tailwind';

import { type GetSelectionMenuOptionsType } from '~/components/Gallery/types';
import { GallerySelectableCardTrigger } from '~/components/v2/components/shared/gallery/GallerySelectableCardTrigger';
import { type GetItemMenuOptions } from '~/constants/MenuOptions';
import { type UseSelectableCardReturnType, useSelectableCard } from '~/hooks/useSelectableCard';
import { useSelectionAwareContextMenuOptions } from '~/hooks/useSelectionAwareContextMenuOptions';
import { type SelectableGalleryItem } from '~/store/selectedItems/types';

export type GallerySelectableCardProps<Item extends SelectableGalleryItem> = Pick<
  ComponentPropsWithoutRef<'div'>,
  'className' | 'onClick' | 'onMouseMove' | 'onMouseLeave'
> & {
  asChild?: boolean;
  children: (
    props: UseSelectableCardReturnType & { setIsMenuOpen: (isOpen: boolean) => void; isMenuOpen: boolean },
  ) => ReactNode;
  getItemMenuOptions: GetItemMenuOptions;
  getSelectionMenuOptions: GetSelectionMenuOptionsType;
  isSelectable: boolean;
  item: Item;
  onClick: () => void;
};

const _GallerySelectableCard = <Item extends SelectableGalleryItem>({
  asChild,
  children,
  className,
  getItemMenuOptions,
  getSelectionMenuOptions,
  isSelectable,
  item,
  onClick,
  onMouseMove,
  onMouseLeave,
  ...restOfProps
}: GallerySelectableCardProps<Item>) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {
    onBlur,
    onFocus,
    handleHover,
    isHovering,
    handleItemClick,
    handleItemDoubleClick,
    handleUnhover,
    confirmItemSelection,
    preSelectItem,
    itemRef,
    isSelected,
    isHighlighted,
  } = useSelectableCard({
    item,
    onClick,
    isSelectable,
  });
  const { options } = useSelectionAwareContextMenuOptions({
    contextMenuOptions: getItemMenuOptions({ trackLocation: 'gallery-item-context-menu' }),
    id: item.id,
    selectionMenuOptions: getSelectionMenuOptions('gallery-multi-select-context-menu'),
  });

  const Component = asChild ? Slot : 'div';

  const handleMouseEnter = useCallback(() => {
    handleHover();
  }, [handleHover]);

  const handleMouseMove = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      handleMouseEnter();
      onMouseMove?.(event);
    },
    [handleMouseEnter, onMouseMove],
  );

  const handleMouseLeave = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      handleUnhover();
      onMouseLeave?.(event);
    },
    [handleUnhover, onMouseLeave],
  );

  const handleKeyUp = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        handleItemClick();
      }
    },
    [handleItemClick],
  );

  /**
   * If the menu is open, we want to keep the hover state active
   */
  const _isHovering = isHovering || isMenuOpen;

  return (
    <ContextMenu
      trigger={
        <Component
          className={cn('group/gallery-selectable-card', className)}
          data-highlighted={isHighlighted}
          data-hovering={isHovering}
          data-is-selectable={isSelectable}
          data-selected={isSelected}
          data-testid="GALLERY_SELECTABLE_CARD"
          onBlur={onBlur}
          onFocus={onFocus}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseMove={handleMouseMove}
          ref={itemRef}
          {...restOfProps}
        >
          <GallerySelectableCardTrigger
            confirmItemSelection={confirmItemSelection}
            isHighlighted={isHighlighted || isSelected}
            isHovering={_isHovering}
            isSelectable={isSelectable}
            onClick={onClick}
            onKeyUp={handleKeyUp}
            onDoubleClick={handleItemDoubleClick}
            preSelectItem={preSelectItem}
            itemType={item.type}
          />
          {children({
            setIsMenuOpen,
            isMenuOpen,
            onBlur,
            onFocus,
            handleHover,
            isHovering: _isHovering,
            handleItemClick,
            handleItemDoubleClick,
            handleUnhover,
            confirmItemSelection,
            preSelectItem,
            itemRef: itemRef,
            isSelected,
            isHighlighted,
          })}
        </Component>
      }
    >
      {renderContextMenuItems({ options })}
    </ContextMenu>
  );
};

export const GallerySelectableCard = memo(_GallerySelectableCard) as typeof _GallerySelectableCard;
